import React, { useEffect, useMemo, useState } from "react";
import api, { API_BASE, extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { Download, Filter, ShieldAlert, FlaskConical, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { ViewScopeTabs, viewToParams } from "@/components/admin/ViewScopeTabs";

const CLASSIFICATIONS = ["official", "demo", "internal", "other"];

const AdminResponses = () => {
  const { t, lang, pickI18n, tProfile, tCountry, tQuestionnaireTitle } = useLang();
  const { user } = useAuth();
  const canExport = user?.role === "admin" || user?.role === "staff";
  const [rows, setRows] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    profile_key: "",
    questionnaire_id: "",
    country: "",
    classification: "",
    date_from: "",
    date_to: "",
  });
  // Phase A17-G — segmented view scope. Owns the new 5-state semantics.
  // Default "production" keeps production-only behaviour on first render.
  // `viewToParams` produces the unified `view_scope` param the backend
  // accepts (production / test_qa / spam / inactive / all).
  const [viewScope, setViewScope] = useState("production");

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== null) params[k] = v;
      });
      Object.assign(params, viewToParams(viewScope));
      const { data } = await api.get("/admin/responses", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(extractErrorMessage(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/profiles").then((r) => setProfiles(r.data || []));
    api.get("/countries").then((r) => setCountries(r.data || []));
    api
      .get("/admin/questionnaires?include_archived=true")
      .then((r) => setQuestionnaires(Array.isArray(r.data) ? r.data : []))
      .catch(() => setQuestionnaires([]));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRows();
  }, [filters, viewScope]);

  const downloadCsv = async () => {
    if (!canExport) return;
    const token = localStorage.getItem("tsw_token");
    const params = new URLSearchParams();
    if (filters.questionnaire_id) params.set("questionnaire_id", filters.questionnaire_id);
    // Phase A17-B/G — export honours the same view scope as the table so
    // production exports never include test/QA rows by default.
    Object.entries(viewToParams(viewScope)).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    const qs = params.toString();
    const url = `${API_BASE}/admin/export/responses.csv${qs ? `?${qs}` : ""}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      // Guard: never call .blob() on an error response — that's how we end up
      // saving an HTML/JSON error body as a fake CSV (or hitting a "body
      // stream already read" if the consumer pipeline retries).
      if (!res.ok) {
        const text = await res.text();
        let detail = text;
        try { detail = JSON.parse(text)?.detail || text; } catch { /* ignore */ }
        throw new Error(`${res.status}: ${detail || t("apiFallback.exportFailed")}`);
      }
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `teamsportworld_responses${filters.questionnaire_id ? `_${filters.questionnaire_id}` : ""}.csv`;
      a.click();
      URL.revokeObjectURL(u);
    } catch (err) {
      toast.error(err?.message || t("apiFallback.exportFailed"));
    }
  };

  const titleOf = (r) => {
    // Phase A3.1-fix-4: route through the centralized helper so the
    // chart/table labels honour UI-side overrides + Intl fallbacks
    // without modifying stored IDs or schemas. Synthesizes a faux
    // questionnaire doc from the projected `questionnaire_title_*`
    // fields so the helper sees the same shape as `/questionnaires`.
    const faux = {
      title_en: r.questionnaire_title_en,
      title_es: r.questionnaire_title_es,
      title_zh: r.questionnaire_title_zh,
      title_it: r.questionnaire_title_it,
      title_fr: r.questionnaire_title_fr,
      title_de: r.questionnaire_title_de,
      title_pt_br: r.questionnaire_title_pt_br,
      title_ja: r.questionnaire_title_ja,
      title_hi: r.questionnaire_title_hi,
      title_ko: r.questionnaire_title_ko,
      title_ru: r.questionnaire_title_ru,
    };
    const out = tQuestionnaireTitle(faux);
    return out || "—";
  };

  const profileLabel = (key) => tProfile(key, profiles.find((x) => x.key === key));

  const classificationBadge = (cls) => {
    if (cls === "official") {
      return (
        <span className="tsw-chip bg-emerald-50 text-emerald-800 border border-emerald-300" data-testid="resp-cls-official">
          <ShieldCheck className="w-3 h-3 inline" /> {t("admin.classificationOfficial")}
        </span>
      );
    }
    if (cls === "demo") {
      return (
        <span className="tsw-chip bg-violet-50 text-violet-700 border border-violet-200" data-testid="resp-cls-demo">
          <FlaskConical className="w-3 h-3 inline" /> {t("admin.classificationDemo")}
        </span>
      );
    }
    if (cls === "internal") {
      return (
        <span className="tsw-chip bg-amber-50 text-amber-700 border border-amber-200" data-testid="resp-cls-internal">
          {t("admin.classificationInternal")}
        </span>
      );
    }
    return (
      <span className="tsw-chip bg-slate-100 text-slate-700" data-testid="resp-cls-other">
        {t("admin.classificationDefault")}
      </span>
    );
  };

  const total = useMemo(() => rows.length, [rows]);

  const sortedCountries = useMemo(() => {
    const collator = new Intl.Collator(lang || "en", { sensitivity: "base" });
    return [...countries].sort((a, b) => {
      const an = tCountry(a.code, a);
      const bn = tCountry(b.code, b);
      return collator.compare(an, bn);
    });
  }, [countries, lang, tCountry]);

  const noTargetingLabel = t("phase2.noFiltersApplied");

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="tsw-overline">{t("admin.title")}</div>
          <h1 className="font-display font-bold text-3xl text-slate-900" data-testid="responses-heading">
            {t("admin.responses")}
          </h1>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            {t("phase2.responsesNonSensitiveBanner")}
          </p>
        </div>
        <button onClick={downloadCsv} className="tsw-btn-secondary" data-testid="responses-export-csv" disabled={!canExport} title={canExport ? "" : t("phase2.exportReservedAdmins")} style={{ opacity: canExport ? 1 : 0.45, cursor: canExport ? "pointer" : "not-allowed" }}>
          <Download className="w-4 h-4" /> {t("admin.exportResponses")}
        </button>
      </div>

      <div className="tsw-card p-4 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="responses-filters">
        <select
          className="tsw-input"
          value={filters.profile_key}
          onChange={(e) => setFilters((f) => ({ ...f, profile_key: e.target.value }))}
          data-testid="responses-filter-profile"
        >
          <option value="">{t("phase2.allProfiles")}</option>
          {profiles.map((p) => (
            <option key={p.key} value={p.key}>{tProfile(p.key, p)}</option>
          ))}
        </select>
        <select
          className="tsw-input col-span-1 sm:col-span-2"
          value={filters.questionnaire_id}
          onChange={(e) => setFilters((f) => ({ ...f, questionnaire_id: e.target.value }))}
          data-testid="responses-filter-questionnaire"
        >
          <option value="">{t("phase2.allQuestionnaires")}</option>
          {questionnaires.map((q) => (
            <option key={q.id} value={q.id}>{tQuestionnaireTitle(q)}</option>
          ))}
        </select>
        <select
          className="tsw-input"
          value={filters.country}
          onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
          data-testid="responses-filter-country"
        >
          <option value="">{t("phase2.allCountries")}</option>
          {sortedCountries.map((c) => (
            <option key={c.code} value={c.code}>{`${tCountry(c.code, c)} (${c.code})`}</option>
          ))}
        </select>
        <select
          className="tsw-input"
          value={filters.classification}
          onChange={(e) => setFilters((f) => ({ ...f, classification: e.target.value }))}
          data-testid="responses-filter-classification"
        >
          <option value="">{t("phase2.anyClassification")}</option>
          {CLASSIFICATIONS.map((c) => (
            <option key={c} value={c}>{t(`admin.classification${c.charAt(0).toUpperCase() + c.slice(1)}`)}</option>
          ))}
        </select>
        <input
          type="date"
          className="tsw-input"
          value={filters.date_from}
          onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
          data-testid="responses-filter-date-from"
        />
        <input
          type="date"
          className="tsw-input"
          value={filters.date_to}
          onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
          data-testid="responses-filter-date-to"
        />
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="responses-summary">
        <Filter className="w-3.5 h-3.5" />
        <span>
          {total} {total === 1 ? t("admin.response") : t("admin.responses")}
        </span>
        {Object.values(filters).every((v) => !v) && <span className="text-slate-400">· {noTargetingLabel}</span>}
        <div className="ms-auto">
          <ViewScopeTabs value={viewScope} onChange={setViewScope} testIdPrefix="responses-view-scope" />
        </div>
      </div>

      <div className="tsw-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="responses-table">
            <thead className="bg-slate-50">
              <tr className="text-start text-xs text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">{t("admin.questionnaires")}</th>
                <th className="px-4 py-3">{t("admin.classification")}</th>
                <th className="px-4 py-3">{t("auth.profile")}</th>
                <th className="px-4 py-3">{t("auth.email")}</th>
                <th className="px-4 py-3">{t("phase2.countryCol")}</th>
                <th className="px-4 py-3">{t("phase2.languageCol")}</th>
                <th className="px-4 py-3">{t("phase2.submittedCol")}</th>
                <th className="px-4 py-3">{t("phase2.sensitiveCol")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`response-row-${r.id}`}>
                  <td className="px-4 py-3 text-slate-900 font-medium max-w-[240px] truncate" title={titleOf(r)}>
                    {titleOf(r)}
                  </td>
                  <td className="px-4 py-3">{classificationBadge(r.classification)}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">
                    <span className="tsw-chip bg-slate-100 text-slate-700">{profileLabel(r.profile_key)}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.user_email || `· ${r.user_id?.slice(0, 8) || "anon"}`}</td>
                  <td className="px-4 py-3 text-slate-700">{r.country ? tCountry(r.country, countries.find((x) => x.code === r.country)) : "—"}</td>
                  <td className="px-4 py-3 text-slate-700">
                    <span className="tsw-chip bg-slate-100 text-slate-700 uppercase">{r.language || "—"}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {r.is_sensitive ? (
                      <span
                        className="tsw-chip bg-amber-50 text-amber-700 border border-amber-200"
                        title={t("phase2.researchOnly")}
                        data-testid={`response-sensitive-${r.id}`}
                      >
                        <ShieldAlert className="w-3 h-3 inline" /> {t("phase2.sensitiveTagShort")}
                      </span>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid="responses-empty">
                    {loading ? t("common.loading") : t("phase2.noResponsesFiltered")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminResponses;
